import type { JSX } from 'react';
import { useState } from 'react';
import type { SerializedToken } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';
import { DEFAULT_LOCALE } from '../../i18n/types.js';
import { ru } from '../../i18n/ru.js';
import { en } from '../../i18n/en.js';

export interface StudioMapPreviewProps {
  mapUrl: string | null;
  bgColor?: string;
  gridSize: number;
  tokens: SerializedToken[];
  /** F-8: the picture is shifted against the grid by this many pixels; the tokens stay on the grid. */
  mapOffsetX?: number;
  mapOffsetY?: number;
  width?: number;
  showLegend?: boolean;
  draftPending?: boolean;
  name?: string;
}

interface MapDimensions {
  width: number;
  height: number;
}

const DICTIONARIES: Record<string, TranslationKeys> = {
  ru,
  en,
};

function translate(key: keyof TranslationKeys): string {
  const dictionary: TranslationKeys = DICTIONARIES[DEFAULT_LOCALE] ?? en;
  return dictionary[key];
}

function formatTokenCount(count: number): string {
  return translate('studio.entity.tokenCount').replace('{count}', String(count));
}

const MIN_TOKEN_DIAMETER = 8;

export function StudioMapPreview(props: StudioMapPreviewProps): JSX.Element {
  const {
    mapUrl,
    bgColor,
    gridSize,
    tokens,
    mapOffsetX = 0,
    mapOffsetY = 0,
    width,
    showLegend,
    draftPending,
    name,
  } = props;

  const [mapDimensions, setMapDimensions] = useState<MapDimensions | undefined>(undefined);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>): void => {
    const img: HTMLImageElement = event.currentTarget;
    setMapDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const visibleTokens: SerializedToken[] = tokens.filter(
    (token: SerializedToken): boolean => token.visible === true,
  );

  const containerClassName: string = draftPending === true
    ? 'relative overflow-hidden border-dashed border-hl/70 border-2'
    : 'relative overflow-hidden';

  const containerStyle: React.CSSProperties = {
    width: width !== undefined ? `${width}px` : '100%',
    backgroundColor: mapUrl === null ? (bgColor ?? undefined) : undefined,
  };

  const canRenderTokens: boolean = mapUrl !== null && mapDimensions !== undefined;

  return (
    <div
      data-testid="studio-map-preview"
      className={containerClassName}
      style={containerStyle}
    >
      {mapUrl !== null ? (
        <img
          src={mapUrl}
          alt={name ?? 'Map preview'}
          className="w-full object-contain"
          onLoad={handleImageLoad}
        />
      ) : (
        <div
          className="w-full"
          style={{ backgroundColor: bgColor ?? undefined, aspectRatio: '16 / 9' }}
        />
      )}

      {canRenderTokens && mapDimensions !== undefined
        ? visibleTokens.map((token: SerializedToken): JSX.Element => {
            const leftPercent: number = ((token.x - mapOffsetX) / mapDimensions.width) * 100;
            const topPercent: number = ((token.y - mapOffsetY) / mapDimensions.height) * 100;
            const rawDiameter: number = token.size * gridSize;
            const diameter: number = rawDiameter < MIN_TOKEN_DIAMETER ? MIN_TOKEN_DIAMETER : rawDiameter;
            const initial: string = token.name.length > 0 ? token.name.charAt(0) : '';
            const tokenStyle: React.CSSProperties = {
              left: `${leftPercent}%`,
              top: `${topPercent}%`,
              width: `${diameter}px`,
              height: `${diameter}px`,
              backgroundColor: token.color,
              transform: 'translate(-50%, -50%)',
            };
            return (
              <div
                key={token.id}
                data-testid="studio-map-preview-token"
                className="absolute flex items-center justify-center rounded-full text-label font-semibold text-fg"
                style={tokenStyle}
              >
                {initial}
              </div>
            );
          })
        : null}

      {draftPending === true ? (
        <span className="absolute right-1 top-1 rounded bg-hl/70 px-1.5 py-0.5 text-label font-semibold uppercase tracking-wide text-fg">
          {translate('studio.ai.badgeReview')}
        </span>
      ) : null}

      {showLegend === true ? (
        <div className="absolute bottom-1 left-1 rounded bg-shade/50 px-2 py-1 text-label text-fg">
          {name !== undefined ? <span className="font-semibold">{name}</span> : null}
          <span className="ml-2 text-fg">{formatTokenCount(visibleTokens.length)}</span>
        </div>
      ) : null}
    </div>
  );
}
